<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import ChatAPI from 'custom/api/chat';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { formatChatMessage, timeLabel } from './format';

const props = defineProps({
  message: { type: Object, required: true },
  people: { type: Array, default: () => [] },
  currentUserId: { type: Number, default: null },
  grouped: { type: Boolean, default: false },
  inThread: { type: Boolean, default: false },
});

const emit = defineEmits(['openThread', 'updated']);

const { t, locale } = useLocoI18n();
const route = useRoute();

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀', '✅'];

const showPicker = ref(false);
const isEditing = ref(false);
const draft = ref('');

const browserLocale = computed(() => locale.value.replace('_', '-'));
const isMine = computed(() => props.message.sender?.id === props.currentUserId);
const html = computed(() =>
  formatChatMessage(props.message, {
    accountId: route.params.accountId,
    people: props.people,
  })
);
const reactions = computed(() => {
  const groups = {};
  (props.message.content_attributes?.reactions || []).forEach(reaction => {
    groups[reaction.emoji] ||= {
      emoji: reaction.emoji,
      names: [],
      mine: false,
    };
    groups[reaction.emoji].names.push(reaction.name);
    if (reaction.user_id === props.currentUserId)
      groups[reaction.emoji].mine = true;
  });
  return Object.values(groups);
});
const links = computed(() => props.message.content_attributes?.links || []);

const run = async request => {
  try {
    const { data } = await request();
    emit('updated', data);
  } catch (error) {
    useAlert(
      t('LOCO.CHAT.ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  }
};

const react = emoji => {
  showPicker.value = false;
  run(() => ChatAPI.react(props.message.channel_id, props.message.id, emoji));
};

const startEdit = () => {
  draft.value = props.message.content || '';
  isEditing.value = true;
};

const saveEdit = async () => {
  if (!draft.value.trim()) return;
  await run(() =>
    ChatAPI.editMessage(props.message.channel_id, props.message.id, draft.value)
  );
  isEditing.value = false;
};

const remove = () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('LOCO.CHAT.DELETE_CONFIRM'))) return;
  run(() => ChatAPI.deleteMessage(props.message.channel_id, props.message.id));
};

const isImage = file => file.content_type?.startsWith('image/');
</script>

<template>
  <div
    class="group relative flex gap-3 px-4 hover:bg-n-alpha-1"
    :class="grouped ? 'py-0.5' : 'pt-2 pb-0.5'"
  >
    <div class="w-8 flex-shrink-0">
      <Avatar
        v-if="!grouped"
        :name="message.sender?.name || ''"
        :src="message.sender?.thumbnail || ''"
        :size="32"
        rounded-full
      />
    </div>

    <div class="flex flex-col min-w-0 flex-1">
      <div v-if="!grouped" class="flex items-baseline gap-2">
        <span class="text-sm font-semibold text-n-slate-12">
          {{ message.sender?.name }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ timeLabel(message.created_at, browserLocale) }}
        </span>
      </div>

      <p v-if="message.deleted" class="text-sm italic text-n-slate-10">
        {{ t('LOCO.CHAT.DELETED') }}
      </p>
      <form
        v-else-if="isEditing"
        class="flex flex-col gap-2 py-1"
        @submit.prevent="saveEdit"
      >
        <textarea
          v-model="draft"
          rows="2"
          class="!mb-0 text-sm"
          @keydown.esc="isEditing = false"
          @keydown.enter.exact.prevent="saveEdit"
        />
        <div class="flex gap-2">
          <button
            type="submit"
            class="px-3 h-7 rounded-md text-xs text-white bg-n-brand"
          >
            {{ t('LOCO.CHAT.SAVE') }}
          </button>
          <button
            type="button"
            class="px-2 h-7 rounded-md text-xs text-n-slate-11 hover:bg-n-alpha-2"
            @click="isEditing = false"
          >
            {{ t('LOCO.CHAT.CANCEL') }}
          </button>
        </div>
      </form>
      <div v-else class="text-sm text-n-slate-12 break-words [&_p]:mb-0">
        <span v-dompurify-html="html" />
        <span v-if="message.edited_at" class="text-xs text-n-slate-10">
          {{ t('LOCO.CHAT.EDITED') }}
        </span>
      </div>

      <div
        v-if="links.length && !message.deleted"
        class="flex flex-wrap gap-2 mt-1"
      >
        <router-link
          v-for="link in links"
          :key="link.id"
          :to="`/app/accounts/${route.params.accountId}/conversations/${link.id}`"
          class="flex items-center gap-2 px-2 py-1 rounded-lg text-xs outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-11 hover:bg-n-alpha-2"
        >
          <Icon icon="i-lucide-message-circle" class="size-3.5" />
          <span class="font-medium text-n-slate-12">#{{ link.id }}</span>
          <span>{{ link.contact_name }}</span>
          <span class="text-n-slate-10"
            >· {{ link.inbox_name }} · {{ link.status }}</span
          >
        </router-link>
      </div>

      <div v-if="message.files?.length" class="flex flex-wrap gap-2 mt-1">
        <a
          v-for="file in message.files"
          :key="file.id"
          :href="file.url"
          target="_blank"
          rel="noopener noreferrer"
          class="block"
        >
          <img
            v-if="isImage(file)"
            :src="file.url"
            :alt="file.filename"
            class="max-h-48 max-w-xs rounded-lg object-cover"
          />
          <span
            v-else
            class="flex items-center gap-2 px-3 py-2 rounded-lg text-xs outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-12"
          >
            <Icon icon="i-lucide-file" class="size-4" />
            {{ file.filename }}
          </span>
        </a>
      </div>

      <div v-if="reactions.length" class="flex flex-wrap gap-1 mt-1">
        <button
          v-for="reaction in reactions"
          :key="reaction.emoji"
          type="button"
          class="flex items-center gap-1 h-6 px-1.5 rounded-full text-xs outline outline-1 -outline-offset-1"
          :class="
            reaction.mine
              ? 'bg-n-brand/10 outline-n-brand text-n-blue-11'
              : 'bg-n-solid-2 outline-n-weak text-n-slate-11'
          "
          :title="reaction.names.join(', ')"
          @click="react(reaction.emoji)"
        >
          {{ reaction.emoji }}
          <span v-if="reaction.names.length > 1">{{
            reaction.names.length
          }}</span>
        </button>
      </div>

      <button
        v-if="!inThread && message.replies_count"
        type="button"
        class="self-start mt-1 text-xs font-medium text-n-blue-11 hover:underline"
        @click="emit('openThread', message)"
      >
        {{
          t(
            'LOCO.CHAT.REPLIES',
            { count: message.replies_count },
            message.replies_count
          )
        }}
      </button>
    </div>

    <div
      v-if="!message.deleted && !isEditing"
      class="absolute -top-3 right-4 hidden group-hover:flex items-center gap-0.5 p-0.5 rounded-lg bg-n-solid-2 shadow-sm outline outline-1 outline-n-weak"
    >
      <div class="relative">
        <button
          type="button"
          class="size-7 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('LOCO.CHAT.REACT')"
          @click="showPicker = !showPicker"
        >
          <Icon icon="i-lucide-smile-plus" class="size-4" />
        </button>
        <div
          v-if="showPicker"
          class="absolute z-20 right-0 top-8 flex gap-1 p-1 rounded-full bg-n-solid-2 shadow-md outline outline-1 outline-n-weak"
        >
          <button
            v-for="emoji in QUICK_REACTIONS"
            :key="emoji"
            type="button"
            class="size-7 rounded-full text-base hover:bg-n-alpha-2"
            @click="react(emoji)"
          >
            {{ emoji }}
          </button>
        </div>
      </div>
      <button
        v-if="!inThread"
        type="button"
        class="size-7 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2"
        :title="t('LOCO.CHAT.REPLY_IN_THREAD')"
        @click="emit('openThread', message)"
      >
        <Icon icon="i-lucide-message-square-reply" class="size-4" />
      </button>
      <template v-if="isMine">
        <button
          type="button"
          class="size-7 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('LOCO.CHAT.EDIT')"
          @click="startEdit"
        >
          <Icon icon="i-lucide-pencil" class="size-4" />
        </button>
        <button
          type="button"
          class="size-7 rounded-md flex items-center justify-center text-n-ruby-11 hover:bg-n-alpha-2"
          :title="t('LOCO.CHAT.DELETE')"
          @click="remove"
        >
          <Icon icon="i-lucide-trash-2" class="size-4" />
        </button>
      </template>
    </div>
  </div>
</template>
